import React, { useState, useEffect } from 'react';
import { policyService, CookieConsent as CookieConsentType } from '../../services/policyService';
import { useAuth } from '../../contexts/AuthContext';

interface CookieConsentProps {
  onAccept?: (consent: CookieConsentType) => void;
  onReject?: () => void;
}

export const CookieConsent: React.FC<CookieConsentProps> = ({ onAccept, onReject }) => {
  const { user } = useAuth();
  const [show, setShow] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [consent, setConsent] = useState<CookieConsentType>({
    necessary: true,
    analytics: false,
    marketing: false,
    preferences: false,
    user_id: user?.id,
    session_id: !user ? policyService.generateSessionId() : undefined,
  });

  useEffect(() => {
    // Check if consent already exists
    const checkExistingConsent = async () => {
      const existing = await policyService.getCookieConsent(
        user?.id,
        !user ? policyService.generateSessionId() : undefined
      );

      if (!existing) {
        // Show banner after 1 second delay
        setTimeout(() => setShow(true), 1000);
      }
    };

    checkExistingConsent();
  }, [user]);

  const handleAcceptAll = async () => {
    const fullConsent: CookieConsentType = {
      ...consent,
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true,
    };

    const success = await policyService.saveCookieConsent(fullConsent);
    if (success) {
      setShow(false);
      onAccept?.(fullConsent);
    }
  };

  const handleAcceptSelected = async () => {
    const success = await policyService.saveCookieConsent(consent);
    if (success) {
      setShow(false);
      onAccept?.(consent);
    }
  };

  const handleRejectAll = async () => {
    const minimalConsent: CookieConsentType = {
      ...consent,
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false,
    };

    const success = await policyService.saveCookieConsent(minimalConsent);
    if (success) {
      setShow(false);
      onReject?.();
    }
  };

  const handleConsentChange = (type: keyof CookieConsentType, checked: boolean) => {
    setConsent(prev => ({ ...prev, [type]: checked }));
  };

  if (!show) return null;

  return (
    <div className={`fixed bottom-0 left-0 right-0 z-50 transform transition-transform duration-300 ${
      show ? 'translate-y-0' : 'translate-y-full'
    }`}>
      <div className="bg-white shadow-2xl border-t-4 border-indigo-600 max-h-[80vh] overflow-auto">
        <div className="max-w-6xl mx-auto p-6">
          <div className="flex items-start justify-between">
            <div className="flex-1 pr-4">
              <div className="flex items-center mb-4">
                <svg className="w-6 h-6 mr-2 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h2 className="text-xl font-semibold text-gray-900">
                  Çerez Tercihleri / Cookie Preferences
                </h2>
              </div>

              <p className="text-sm text-gray-700 mb-3">
                Bu web sitesi, deneyiminizi geliştirmek için çerezleri kullanır. Çerezler, web
                sitesinin düzgün çalışması, güvenlik, daha iyi kullanıcı deneyimi ve size özel
                içerik sunmak için kullanılır.
              </p>

              <p className="text-sm text-gray-600 italic mb-4">
                This website uses cookies to enhance your experience. Cookies are used for the
                proper functioning of the website, security, better user experience, and to provide
                personalized content.
              </p>

              <button
                onClick={() => setShowDetails(!showDetails)}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 mb-4"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                {showDetails ? 'Gizle / Hide' : 'Detayları Göster / Show Details'}
              </button>

              <div className={`transition-all duration-300 overflow-hidden ${
                showDetails ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
              }`}>
                <div className="space-y-4 mb-4">
                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      checked={consent.necessary}
                      disabled
                      className="w-4 h-4 mt-1 mr-3 text-gray-400 border-gray-300 rounded cursor-not-allowed"
                    />
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">Gerekli / Necessary</h4>
                      <p className="text-xs text-gray-500">
                        Web sitesinin temel işlevleri için gereklidir. / Required for basic website functionality.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      checked={consent.analytics}
                      onChange={(e) => handleConsentChange('analytics', e.target.checked)}
                      className="w-4 h-4 mt-1 mr-3 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                    />
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">Analitik / Analytics</h4>
                      <p className="text-xs text-gray-500">
                        Ziyaretçi istatistiklerini toplamak için. / To collect visitor statistics.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      checked={consent.marketing}
                      onChange={(e) => handleConsentChange('marketing', e.target.checked)}
                      className="w-4 h-4 mt-1 mr-3 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                    />
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">Pazarlama / Marketing</h4>
                      <p className="text-xs text-gray-500">
                        Size özel reklamlar göstermek için. / To show personalized ads.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      checked={consent.preferences}
                      onChange={(e) => handleConsentChange('preferences', e.target.checked)}
                      className="w-4 h-4 mt-1 mr-3 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                    />
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">Tercihler / Preferences</h4>
                      <p className="text-xs text-gray-500">
                        Dil ve bölge tercihlerinizi hatırlamak için. / To remember your language and region preferences.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <p className="text-xs text-gray-500">
                Daha fazla bilgi için{' '}
                <a href="/policies?policy=cookie_policy" className="text-indigo-600 underline hover:text-indigo-500">
                  Çerez Politikamızı
                </a>{' '}
                ve{' '}
                <a href="/policies?policy=privacy_policy" className="text-indigo-600 underline hover:text-indigo-500">
                  Gizlilik Politikamızı
                </a>{' '}
                inceleyebilirsiniz.
              </p>
            </div>

            <button
              onClick={() => setShow(false)}
              className="p-2 ml-2 text-gray-400 hover:text-gray-600 focus:outline-none"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="flex flex-wrap gap-3 mt-6 justify-end">
            <button
              onClick={handleRejectAll}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Reddet / Reject
            </button>
            {showDetails && (
              <button
                onClick={handleAcceptSelected}
                className="px-4 py-2 text-sm font-medium text-indigo-700 bg-indigo-100 border border-indigo-300 rounded-md hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Seçilenleri Kabul Et / Accept Selected
              </button>
            )}
            <button
              onClick={handleAcceptAll}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Tümünü Kabul Et / Accept All
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};